import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../styles/AdminNotPageFormList.css';
import Toast from './Toast';
import ButtonRegular from './ui/ButtonRegular';
import ButtonAdditional from './ui/ButtonAdditional';

const AdminNotPageFormList = () => {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newFormId, setNewFormId] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [toast, setToast] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalForms, setTotalForms] = useState(0);

    const formsPerPage = 20;

    const statusLabels = {
        open: 'Nieuzupełniony',
        closed: 'Oczekuje na płatność',
        paid: 'Opłacone',
        sent: 'Wysłany',
        canceled: 'Anulowany',
        paiedByTransfer: 'Opłacone przelewem',
        readyForDelivery: 'Przygotowane do wysyłki',
        completed: 'Zrealizowane',
        returned: 'Zwrócone',
        laterSchipment: 'Wysyłka w innym terminie',
    };

    const getStatusLabel = (status) =>
        statusLabels[status] || statusLabels.open;

    const fetchForms = async (page = 1) => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/forms?page=${page}&limit=${formsPerPage}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    },
                }
            );

            setForms(response.data.forms);
            setCurrentPage(response.data.currentPage);
            setTotalPages(response.data.totalPages);
            setTotalForms(response.data.totalForms);
        } catch (err) {
            console.error('Błąd podczas ładowania formularzy:', err);
            setError('Błąd podczas ładowania formularzy');
        } finally {
            setLoading(false);
        }
    };

    const createNewForm = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/forms`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    },
                }
            );

            setNewFormId(response.data._id);
            await fetchForms(1);
            setToast('✅ Utworzono nowy formularz');
        } catch (err) {
            console.error('Błąd podczas tworzenia formularza:', err);
            setToast('❌ Błąd podczas tworzenia formularza');
        }
    };

    // Zmiana statusu przez nowy endpoint PATCH
    const handleStatusChange = async (form, newStatus) => {
        const currentStatusText = getStatusLabel(form.status);
        const newStatusText = getStatusLabel(newStatus);

        const isConfirmed = window.confirm(
            `Czy na pewno chcesz zmienić status formularza nr ${form.formNumber} z "${currentStatusText}" na "${newStatusText}"?`
        );

        if (!isConfirmed) return;

        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/forms/${form._id}/status`,
                { status: newStatus },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    },
                }
            );

            const updatedStatus = response.data.status;

            setForms((prevForms) =>
                prevForms.map((item) =>
                    item._id === form._id
                        ? { ...item, status: updatedStatus }
                        : item
                )
            );

            setToast('✅ Zmieniono status formularza');
        } catch (err) {
            console.error(
                'Błąd podczas zmiany statusu:',
                err.response?.data || err
            );

            setToast(
                err.response?.data?.message ||
                '❌ Nie udało się zmienić statusu'
            );
        }
    };

    const copyLink = (id) => {
        const fullUrl = `${window.location.origin}/form/${id}`;

        if (document.hasFocus()) {
            navigator.clipboard
                .writeText(fullUrl)
                .then(() => {
                    setCopiedId(id);
                    setTimeout(() => setCopiedId(null), 2000);
                    setToast('📋 Skopiowano link do schowka');
                })
                .catch((err) => {
                    console.error('Błąd podczas kopiowania linku:', err);
                    setToast('❌ Nie udało się skopiować linku');
                });
        } else {
            setToast('⚠️ Aktywuj okno przeglądarki, aby skopiować link.');
        }
    };

    useEffect(() => {
        fetchForms(1);
    }, []);

    const getRowClass = (status) => {
        switch (status) {
            case 'closed':
                return 'row-status-closed';
            case 'paid':
                return 'row-status-paid';
            case 'sent':
                return 'row-status-sent';
            case 'canceled':
                return 'row-status-canceled';
            case 'paiedByTransfer':
                return 'row-status-paiedByTransfer';
            case 'readyForDelivery':
                return 'row-status-readyForDelivery';
            case 'completed':
                return 'row-status-completed';
            case 'returned':
                return 'row-status-returned';
            case 'laterSchipment':
                return 'row-status-laterSchipment';
            default:
                return 'row-status-progress';
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1 && !loading) {
            fetchForms(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages && !loading) {
            fetchForms(currentPage + 1);
        }
    };

    return (
        <div className="admin-card">
            <h2 style={{ color: 'var(--accent-dark)' }}>
                Zarządzanie formularzami
            </h2>

            <ButtonRegular onClick={createNewForm}>
                ➕ Utwórz nowy formularz
            </ButtonRegular>

            {newFormId && (
                <p>
                    Wygenerowano nowy formularz.{' '}
                    <Link to={`/form/admin/${newFormId}`}>
                        Wprowadź produkty
                    </Link>
                </p>
            )}

            {loading ? (
                <p>Ładowanie formularzy...</p>
            ) : error ? (
                <p>{error}</p>
            ) : (
                <>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Nr</th>
                                <th>Imię i nazwisko</th>
                                <th>Data utworzenia</th>
                                <th>Status</th>
                                <th>Akcje</th>
                                <th>Link</th>
                            </tr>
                        </thead>

                        <tbody>
                            {forms.map((form) => (
                                <tr
                                    key={form._id}
                                    className={getRowClass(form.status)}
                                >
                                    <td>{form.formNumber}</td>

                                    {form.customerData ? (
                                        <td>
                                            {form.customerData.firstName +
                                                ' ' +
                                                form.customerData.lastName}
                                        </td>
                                    ) : (
                                        <td>-</td>
                                    )}

                                    <td>
                                        {new Date(form.createdAt).toLocaleString()}
                                    </td>

                                    <td>
                                        <select
                                            value={form.status || 'open'}
                                            onChange={(e) =>
                                                handleStatusChange(
                                                    form,
                                                    e.target.value
                                                )
                                            }
                                            className="status-select"
                                        >
                                            <option value="open">
                                                Nieuzupełniony
                                            </option>
                                            <option value="closed">
                                                Oczekuje na płatność
                                            </option>
                                            <option value="paid">
                                                Opłacone
                                            </option>
                                            <option value="sent">
                                                Wysłany
                                            </option>
                                            <option value="canceled">
                                                Anulowany
                                            </option>
                                            <option value="paiedByTransfer">
                                                Opłacone przelewem
                                            </option>
                                            <option value="readyForDelivery">
                                                Przygotowane do wysyłki
                                            </option>
                                            <option value="completed">
                                                Zrealizowane
                                            </option>
                                            <option value="returned">
                                                Zwrócone
                                            </option>
                                            <option value="laterSchipment">
                                                Wysyłka w innym terminie
                                            </option>
                                        </select>
                                    </td>

                                    <td className="actions">
                                        <div className="action-group">
                                                <Link
                                                    className="btn-additional small"
                                                    to={`/form/admin/show/${form._id}`}
                                                >
                                                    Podgląd
                                                </Link>

                                            {(form.status === 'paid' ||
                                                form.status === 'paiedByTransfer') && (
                                                <Link
                                                    className="btn-additional small"
                                                    to={`/form/admin/delivery/${form._id}`}
                                                >
                                                    Generowanie Inpost
                                                </Link>
                                            )}

                                            {form.status === 'open' && (
                                                <Link
                                                    className="btn-regular small"
                                                    to={`/form/admin/${form._id}`}
                                                >
                                                    Edycja
                                                </Link>
                                            )}
                                        </div>
                                    </td>

                                    <td>
                                        <ButtonAdditional
                                            className="small"
                                            onClick={() => copyLink(form._id)}
                                        >
                                            📋 Skopiuj link
                                        </ButtonAdditional>

                                        {copiedId === form._id && (
                                            <p
                                                style={{
                                                    color: 'green',
                                                    margin: '0 0 0 8px',
                                                    fontWeight: '600',
                                                }}
                                            >
                                                Skopiowano!
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="pagination">
                        <ButtonAdditional
                            className="small"
                            onClick={handlePreviousPage}
                            disabled={currentPage <= 1 || loading}
                        >
                            ← Poprzednia
                        </ButtonAdditional>

                        <span>
                            Strona {currentPage} z {totalPages}
                            {' '}({totalForms} formularzy)
                        </span>

                        <ButtonAdditional
                            className="small"
                            onClick={handleNextPage}
                            disabled={currentPage >= totalPages || loading}
                        >
                            Następna →
                        </ButtonAdditional>
                    </div>
                </>
            )}

            {toast && (
                <Toast
                    message={toast}
                    onClose={() => setToast('')}
                />
            )}
        </div>
    );
};

export default AdminNotPageFormList;